import React from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import styled, { css, keyframes } from 'styled-components'

import { useReactHookFormFormError } from '@libs/typescript-utils'

import { WarningText } from '@components/ui/WarningText'

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const StyledWarningText = styled(WarningText)<{ $withFade?: boolean }>`
  ${({ $withFade }) =>
    $withFade &&
    css`
      animation: ${fadeInAnimation} 100ms ease-in-out;
      opacity: 1;
    `}
`

interface ReactHookFormErrorProps {
  actionError?: Nullable<string>
  className?: string
  hideIcon?: boolean
  withFade?: boolean
}

export function ReactHookFormError({
  actionError,
  className,
  hideIcon = false,
  withFade = false,
}: ReactHookFormErrorProps) {
  const { clearErrors } = useFormContext()
  const values = useWatch()
  const error = useReactHookFormFormError()
  const isErrorVisibleRef = React.useRef(false)

  React.useEffect(() => {
    if (error) {
      isErrorVisibleRef.current = true
    }
  }, [error])

  // clear error when values change
  React.useEffect(() => {
    if (isErrorVisibleRef.current) {
      isErrorVisibleRef.current = false
      clearErrors('_error')
      clearErrors('Error')
    }
  }, [clearErrors, values])

  if (!error && !actionError) {
    return null
  }

  return (
    <StyledWarningText $withFade={withFade} className={className} hideIcon={hideIcon}>
      {actionError || error?.message}
    </StyledWarningText>
  )
}
